import PropTypes from 'prop-types';

// Hooks
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';

// Components
import Tooltip from '@/components/base/Tooltip';
import SocialProfilesAndGroupsCondensedList from '@/components/composite/SocialProfiles/SocialProfilesAndGroupsCondensedList';
import { MediaViewerViewPostOnNetwork } from '@/components/composite/MediaViewer/MediaViewerViewPostOnNetwork';

const MediaViewerHeader = ({ post, user, isLoading, socialProfile, className }) => {
    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone);

    return (
        <div className={className}>
            {!isLoading ? (
                <div className="flex w-full items-center">
                    <MediaViewerHeader.Avatars socialProfile={socialProfile} post={post} />
                    <div className="flex-1 px-3">
                        <MediaViewerHeader.Usernames socialProfile={socialProfile} post={post} />
                        <p className="mt-1 text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {formatDateTime(
                                post?.created ?? post?.published_at,
                                user?.settings.date_format
                            )}{' '}
                            {formatDateTime(
                                post?.created ?? post?.published_at,
                                user?.settings.time_format
                            )}
                        </p>
                    </div>
                    {post?.permalink ? (
                        <MediaViewerViewPostOnNetwork
                            mediaViewerPost={post}
                            socialProfile={socialProfile}
                        />
                    ) : null}
                </div>
            ) : (
                <div className="flex w-full animate-pulse items-center">
                    <span className="block h-[3.125rem] w-[3.125rem] rounded-full bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
                    <div className="flex-1 px-5">
                        <span className="block w-3/4 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                        <span className="mt-1 block w-1/2 rounded bg-icoGray-300 py-1.5 dark:bg-icoDarkMode-deepBlack" />
                    </div>
                    <div className="w-8">
                        <span className="block w-full rounded bg-icoGray-300 py-4 dark:bg-icoDarkMode-deepBlack" />
                    </div>
                </div>
            )}
        </div>
    );
};

MediaViewerHeader.propTypes = {
    post: PropTypes.object,
    user: PropTypes.object,
    isLoading: PropTypes.bool,
    className: PropTypes.string,
    socialProfile: PropTypes.object
};

export default MediaViewerHeader;

const Usernames = ({ socialProfile, post }) => {
    if (post?.has_collaborators) {
        const usernames = [
            socialProfile.slug,
            ...post.collaborators
                .filter((c) => c.has_accepted_collaboration)
                .map((collaborator) => {
                    if (collaborator.has_accepted_collaboration) return collaborator.username;
                })
        ];

        if (usernames.length === 2) {
            return (
                <p className="text-sm" data-testid="usernames">
                    {usernames.join(' & ')}
                </p>
            );
        }

        if (usernames.length > 2) {
            return (
                <p className="flex space-x-1 text-sm" data-testid="usernames">
                    <span>{usernames.slice(0, 2).join(', ')} &</span>
                    <Tooltip
                        content={usernames.slice(2, usernames.length).join(' & ')}
                        placement="top">
                        <p> {usernames.length - 2} more</p>
                    </Tooltip>
                </p>
            );
        }
    }

    return (
        <p className="text-sm" data-testid="usernames">
            {socialProfile?.slug}
        </p>
    );
};

Usernames.propTypes = {
    socialProfile: PropTypes.object,
    post: PropTypes.object
};
MediaViewerHeader.Usernames = Usernames;

const Avatars = ({ socialProfile, post }) => {
    let collaborators = [];
    if (post?.has_collaborators) {
        collaborators = post?.collaborators
            .filter((c) => c.has_accepted_collaboration)
            .map((collaborator) => {
                return {
                    external_api_data: { avatar: collaborator?.profile_picture_url ?? '' },
                    slug: collaborator.username,
                    social_network_type: ''
                };
            });
    }

    const list = [socialProfile, ...(collaborators ?? [])];

    return (
        <SocialProfilesAndGroupsCondensedList
            list={list}
            maxDisplayed={4}
            size="base"
            condensed="-ml-6"
            profileNetworkType={list?.length > 1 ? false : true}
        />
    );
};

Avatars.propTypes = {
    socialProfile: PropTypes.object,
    post: PropTypes.object
};
MediaViewerHeader.Avatars = Avatars;
